import { Box, useTheme } from '@mui/material';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import Button from '../../././Button';
import { tryParseFloat } from '../../../lib/numbers';
import { LibroTheme } from '../../../theme/types';
import ControlledMap from '../../Map/components/ControlledMap';
import { GeometryType } from '../../Map/lib/geometry';
import { getStyles } from '../../Map/lib/helpers';
import { Layer, MapInteractionCallback, MapVariant, SearchSelectCallback } from '../../Map/lib/types';
import { InputRenderer } from '../types';

const buildLocation = (lat: number | undefined, lon: number | undefined) => {
  if (lat && lon) {
    return new Point(fromLonLat([lon, lat]));
  }

  return undefined;
};

const buildFeature = (location: Point, theme: LibroTheme) => {
  const { hoverStyle, style } = getStyles('http://fontawesome.io/icon/map-marker', theme);
  const feature = new Feature(location);

  feature.setProperties({
    hoverStyle,
    image: 'http://fontawesome.io/icon/map-marker',
    style,
  });

  return feature;
};

const LocationInput = ({ inputProps }: InputRenderer) => {
  const theme = useTheme();
  const lat = useWatch({ name: 'lat' });
  const lon = useWatch({ name: 'lon' });
  const { setValue } = useFormContext();

  const [location, setLocation] = React.useState<Point | undefined>(() =>
    buildLocation(tryParseFloat(lat), tryParseFloat(lon)),
  );
  const [locationLayer, setLocationLayer] = React.useState<Layer[]>([{ features: [] }]);

  React.useEffect(() => {
    setLocation(buildLocation(lat, lon));
  }, [inputProps.value, lat, lon]);

  React.useEffect(() => {
    if (location) {
      const features = [buildFeature(location, theme)];

      setLocationLayer([{ features }]);
    } else {
      setLocationLayer([{ features: [] }]);
    }
  }, [location]);

  const handleNewLocation = (newLon?: number, newLat?: number) => {
    setValue('lat', newLat ?? '');
    setValue('lon', newLon ?? '');

    setLocation(buildLocation(newLat, newLon));
  };

  const handleInteraction = React.useCallback<MapInteractionCallback>((newGeometry) => {
    if (newGeometry.type === GeometryType.POINT) {
      const { lat: newLat, lon: newLon } = newGeometry.points[0];
      handleNewLocation(newLon, newLat);
    }
  }, []);

  const handleSearchSelect: SearchSelectCallback = ([newLon, newLat]) => {
    handleNewLocation(newLon, newLat);
  };

  const clearValue = () => {
    handleNewLocation(undefined, undefined);
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          marginBottom: '1rem',
          position: 'relative',
        }}
      >
        <ControlledMap
          layers={locationLayer}
          variant={MapVariant.Default}
          onInteraction={handleInteraction}
          onSearchSelect={handleSearchSelect}
        />
        {location && (
          <Box sx={{ position: 'absolute', top: 0, zIndex: 9, right: 0 }}>
            <Button variant="text" onClick={clearValue}>
              X
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default LocationInput;
